import { useQuery } from '@tanstack/react-query'
import { api } from '../../../lib/api'

/* ───── Types ───── */

export interface GroupDetail {
  /** Internal numeric ID */
  id: string
  /** sqid-encoded public ID */
  sqid: string
  /** Group display name */
  name: string
  /** Group identifier (invite link, group ID, etc.) */
  identifier: string
  /** Platform: telegram, whatsapp, discord, signal, etc. */
  platform: string
  /** ISO 3166-1 alpha-2 country code */
  country: string
  /** Number of tracked members */
  member_count: number
  /** Number of messages collected */
  message_count: number
  /** ISO 8601 last activity timestamp */
  last_activity: string
  /** Status: '1' (active) or '0' (inactive) */
  status: string
  /** Free-text description */
  description: string
  /** Risk score (0-10) */
  risk_score: number
  /** Group avatar / image URL */
  image_url: string
  /** ISO 8601 creation timestamp */
  created_at: string
  /** ISO 8601 last-update timestamp */
  updated_at: string
  /** Platform invite link (used for Copy Group Link quick action) */
  invite_link?: string
  /** Whether monitoring is active for this group */
  monitoring_active: boolean
}

interface GroupDetailResponse {
  group: GroupDetail
}

/* ───── Query Keys ───── */

export const groupDetailQueryKey = (id: string) => ['group', id] as const

/* ───── Hook ───── */

/**
 * TanStack Query hook for fetching group detail.
 * GET /api/v1/groups/{id}
 */
export function useGroupDetail(id: string) {
  const query = useQuery({
    queryKey: groupDetailQueryKey(id),
    queryFn: ({ signal }) =>
      api<GroupDetailResponse>(`/v1/groups/${id}`, { signal }),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return {
    group: query.data?.group ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
